import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap, faBook, faRocket, faCheckCircle, faStar,
  faArrowRight, faCrown, faChartLine, faUsers, faRobot, faBookOpen,
  faBolt, faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import LocalizedLink from '../components/LocalizedLink';
import { getExamUrl } from '../utils/slugify';

interface Exam {
  id: number;
  name: string;
  description: string;
  is_free_available: boolean;
  category?: string;
  badge_image?: string;
}

interface Subcategory {
  subcategory: string | null;
  exams: Exam[];
}

interface Category {
  category: string;
  subcategories: Subcategory[];
}

const Home: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [freeModeEnabled, setFreeModeEnabled] = useState(false);

  const examCount = useMemo(
    () => categories.reduce((n, c) => n + c.subcategories.reduce((s, sub) => s + sub.exams.length, 0), 0),
    [categories]
  );

  useEffect(() => {
    fetchCategories();
    fetchFreeModeStatus();
  }, []);

  const fetchFreeModeStatus = async () => {
    try {
      const response = await api.get('/settings/free-mode/status');
      setFreeModeEnabled(response.data.freeModeEnabled || false);
    } catch {
      setFreeModeEnabled(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/exams/categories');
      setCategories(response.data.categories || []);
    } catch {
      try {
        const response = await api.get('/exams');
        const exams: Exam[] = response.data.exams || [];
        const grouped = exams.reduce((acc, exam) => {
          const cat = exam.category || 'Other';
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push(exam);
          return acc;
        }, {} as Record<string, Exam[]>);
        setCategories(
          Object.entries(grouped).map(([category, catExams]) => ({
            category,
            subcategories: [{ subcategory: null, exams: catExams }],
          })).sort((a, b) => a.category.localeCompare(b.category))
        );
      } catch {
        /* empty */
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div
          className="w-10 h-10 rounded-full border-2 border-transparent animate-spin"
          style={{ borderTopColor: 'var(--gp-brand)', borderRightColor: 'var(--gp-brand)' }}
          role="status"
          aria-label={t('common.loading')}
        />
      </div>
    );
  }

  return (
    <>
      <SEO
        title={t('home.seo.title')}
        description={t('home.seo.description')}
        keywords={t('home.seo.keywords')}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'GlobalPreps',
            description: t('home.seo.description'),
            url: 'https://globalpreps.com',
            applicationCategory: 'EducationalApplication',
            operatingSystem: 'Web',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'GlobalPreps',
            url: 'https://globalpreps.com',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://globalpreps.com/certifications?q={search_term_string}',
              },
              'query-input': 'required name=search_term_string',
            },
          },
        ]}
        breadcrumbs={[{ name: t('nav.home'), url: '/' }]}
      />

      <div className="gp-page">
        {/* ── Hero ── */}
        <section className="gp-container pt-6 sm:pt-8 pb-8 sm:pb-10" aria-labelledby="home-hero-heading">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            <div className="lg:col-span-7 gp-animate-in">
              <p className="font-display text-2xl sm:text-3xl font-semibold mb-3 gp-brand-text" aria-hidden="true">
                GlobalPreps
              </p>
              <span className="gp-badge gp-badge-brand mb-3">
                <FontAwesomeIcon icon={faBolt} aria-hidden="true" />
                {t('home.nicheBadge')}
              </span>
              <h1 id="home-hero-heading" className="gp-heading-xl mb-3">
                {t('home.title')}
              </h1>
              <p className="text-base gp-text-muted max-w-lg mb-5 leading-relaxed">
                {freeModeEnabled ? t('home.freeModeSubtitle') : t('home.subtitle')}
              </p>
              {!user && (
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <LocalizedLink to="/register" className="gp-btn-accent">
                    {t('home.getStarted')}
                    <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                  </LocalizedLink>
                  <LocalizedLink to="/learn" className="gp-btn-ghost">
                    <FontAwesomeIcon icon={faRobot} aria-hidden="true" />
                    {t('home.exploreLearning')}
                  </LocalizedLink>
                </div>
              )}
              {user && (
                <LocalizedLink to="/dashboard" className="gp-btn-primary">
                  {t('nav.dashboard')}
                  <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </LocalizedLink>
              )}
            </div>

            <div className="lg:col-span-5 gp-animate-in gp-animate-in-delay-1">
              <div className="gp-hero-panel p-4 sm:p-5">
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="gp-stat">
                    <div className="gp-stat-value">{examCount || '—'}</div>
                    <div className="gp-stat-label">{t('home.stats.exams')}</div>
                  </div>
                  <div className="gp-stat">
                    <div className="gp-stat-value">5</div>
                    <div className="gp-stat-label">{t('home.stats.aiTracks')}</div>
                  </div>
                  <div className="gp-stat">
                    <div className="gp-stat-value text-sm sm:text-base leading-tight font-display font-bold" style={{ color: 'var(--gp-brand)' }}>
                      {freeModeEnabled ? t('home.stats.freeOpen') : t('home.stats.freeStart')}
                    </div>
                    <div className="gp-stat-label">{t('home.stats.practice')}</div>
                  </div>
                </div>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gp-ink-muted)' }}>
                  {[t('home.heroPoint1'), t('home.heroPoint2'), t('home.heroPoint3')].map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="mt-0.5 shrink-0 text-xs"
                        style={{ color: 'var(--gp-brand)' }}
                        aria-hidden="true"
                      />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── Paths ── */}
        <section className="gp-container gp-section" aria-labelledby="home-paths-heading">
          <div className="mb-4">
            <h2 id="home-paths-heading" className="gp-heading-lg">
              {t('home.pathsTitle')}
            </h2>
            <p className="gp-text-muted mt-1 text-sm">{t('home.pathsSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <LocalizedLink
              to="/certifications"
              className="gp-card gp-card-interactive gp-path-card-featured p-4 flex flex-col justify-between min-h-[140px]"
            >
              <div>
                <FontAwesomeIcon icon={faGraduationCap} className="text-xl mb-2 opacity-90" aria-hidden="true" />
                <h3 className="font-display text-lg font-semibold mb-1">{t('home.pathCerts.title')}</h3>
                <p className="gp-path-desc text-sm">{t('home.pathCerts.description')}</p>
              </div>
              <span className="gp-path-cta mt-3 inline-flex items-center gap-1.5 font-semibold text-sm">
                {t('home.pathCerts.cta')}
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </span>
            </LocalizedLink>

            <LocalizedLink
              to="/learn#ai-courses"
              className="gp-card gp-card-interactive p-4 flex flex-col justify-between min-h-[140px]"
              style={{ borderColor: 'var(--gp-ai)' }}
            >
              <div>
                <span className="gp-badge gp-badge-ai mb-2">AI</span>
                <h3 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                  {t('home.pathAi.title')}
                </h3>
                <p className="text-sm gp-text-muted">{t('home.pathAi.description')}</p>
              </div>
              <span className="mt-3 text-sm font-semibold inline-flex items-center gap-1" style={{ color: 'var(--gp-ai)' }}>
                {t('home.pathAi.cta')}
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" aria-hidden="true" />
              </span>
            </LocalizedLink>

            <LocalizedLink
              to="/learn#study-guides"
              className="gp-card gp-card-interactive p-4 flex flex-col justify-between min-h-[140px]"
            >
              <div>
                <FontAwesomeIcon icon={faBookOpen} className="text-lg mb-2" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                <h3 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                  {t('home.pathGuides.title')}
                </h3>
                <p className="text-sm gp-text-muted">{t('home.pathGuides.description')}</p>
              </div>
              <span className="mt-3 text-sm font-semibold inline-flex items-center gap-1" style={{ color: 'var(--gp-brand)' }}>
                {t('home.pathGuides.cta')}
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" aria-hidden="true" />
              </span>
            </LocalizedLink>
          </div>
        </section>

        {/* ── Features strip ── */}
        <section className="gp-container gp-section" aria-label={t('home.featuresLabel')}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: faBook, title: t('home.freePractice'), desc: t('home.freePracticeDescription') },
              { icon: faRocket, title: t('home.unlimitedAccess'), desc: t('home.unlimitedAccessDescription') },
              { icon: faChartLine, title: t('home.detailedAnalytics'), desc: t('home.detailedAnalyticsDescription') },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="gp-card flex gap-3 items-start py-3.5">
                <div
                  className="w-9 h-9 rounded-md shrink-0 flex items-center justify-center"
                  style={{ background: 'var(--gp-brand-soft)' }}
                >
                  <FontAwesomeIcon icon={icon} className="text-sm" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm mb-0.5" style={{ color: 'var(--gp-ink)' }}>{title}</h3>
                  <p className="text-xs gp-text-muted leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Exams catalog ── */}
        <section className="gp-container gp-section" aria-labelledby="home-exams-heading">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <h2 id="home-exams-heading" className="gp-heading-lg">{t('certifications.title')}</h2>
              <p className="gp-text-muted mt-1 text-sm">{t('certifications.description')}</p>
            </div>
            <LocalizedLink to="/certifications" className="gp-btn-ghost text-sm shrink-0">
              {t('home.viewAllCerts')}
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </LocalizedLink>
          </div>

          {categories.length === 0 ? (
            <p className="gp-text-muted text-center py-8 text-sm">{t('certifications.noCertifications')}</p>
          ) : (
            <div className="space-y-8">
              {categories.map((cat) => (
                <div key={cat.category}>
                  <h3
                    className="font-display text-base font-semibold mb-3 flex items-center gap-2 pb-2 border-b"
                    style={{ color: 'var(--gp-ink)', borderColor: 'var(--gp-border)' }}
                  >
                    <FontAwesomeIcon icon={faLayerGroup} className="text-sm" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                    {cat.category}
                  </h3>
                  {cat.subcategories.map((subcategory, subIndex) => (
                    <div key={subIndex} className="space-y-3">
                      {cat.subcategories.length > 1 && subcategory.subcategory && (
                        <h4 className="text-xs font-bold uppercase tracking-wider gp-text-muted ml-0.5">
                          {subcategory.subcategory}
                        </h4>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {subcategory.exams.map((exam) => (
                          <Link key={exam.id} to={getExamUrl(exam.id, exam.name)} className="gp-exam-card group">
                            <div className="gp-exam-card-badge">
                              {exam.badge_image ? (
                                <img
                                  src={`/${exam.badge_image}`}
                                  alt=""
                                  className="w-16 h-16 object-contain"
                                  loading="lazy"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div
                                  className="w-14 h-14 rounded-lg flex items-center justify-center"
                                  style={{ background: 'linear-gradient(135deg, var(--gp-brand), #17838d)' }}
                                >
                                  <FontAwesomeIcon icon={faGraduationCap} className="text-white text-xl" aria-hidden="true" />
                                </div>
                              )}
                            </div>
                            <div className="gp-exam-card-body">
                              <h4
                                className="font-semibold text-sm mb-1 line-clamp-2 group-hover:underline"
                                style={{ color: 'var(--gp-ink)' }}
                              >
                                {exam.name}
                              </h4>
                              <p className="text-xs gp-text-muted line-clamp-2 mb-3 flex-1">{exam.description}</p>
                              <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: 'var(--gp-border)' }}>
                                {exam.is_free_available ? (
                                  <span className="gp-tag-free">{t('home.freeAvailable')}</span>
                                ) : (
                                  <span className="gp-tag-premium">
                                    <FontAwesomeIcon icon={faCrown} className="mr-1" aria-hidden="true" />
                                    {t('home.premium')}
                                  </span>
                                )}
                                <FontAwesomeIcon
                                  icon={faArrowRight}
                                  className="text-xs transition-transform group-hover:translate-x-1"
                                  style={{ color: 'var(--gp-brand)' }}
                                  aria-hidden="true"
                                />
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── CTA ── */}
        {!user && (
          <section className="gp-container gp-section">
            <div
              className="gp-cta-band flex flex-col md:flex-row md:items-center md:justify-between gap-4"
              style={{
                background: 'linear-gradient(135deg, var(--gp-ink) 0%, #1a3340 55%, var(--gp-brand) 100%)',
                color: '#fff',
              }}
            >
              <div>
                <h2 className="font-display text-xl font-semibold mb-1 flex items-center gap-2">
                  <FontAwesomeIcon icon={faCrown} aria-hidden="true" />
                  {t('home.upgradeTitle')}
                </h2>
                <p className="opacity-85 text-sm max-w-lg">{t('home.upgradeDescription')}</p>
              </div>
              <Link to="/register" className="gp-btn-accent shrink-0">
                {t('auth.signUp')}
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            </div>
          </section>
        )}

        {/* ── Why us ── */}
        <section className="gp-container pb-10 sm:pb-12">
          <div className="gp-card p-5 sm:p-6" style={{ background: 'var(--gp-bg-elevated)' }}>
            <h2 className="gp-heading-lg text-center mb-5">{t('home.whyChooseUs')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: faCheckCircle, title: t('home.realExamQuestions'), desc: t('home.realExamQuestionsDescription') },
                { icon: faStar, title: t('home.detailedExplanations'), desc: t('home.detailedExplanationsDescription') },
                { icon: faChartLine, title: t('home.trackYourProgress'), desc: t('home.trackYourProgressDescription') },
                { icon: faUsers, title: t('home.personalProgress'), desc: t('home.personalProgressDescription') },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex gap-3">
                  <div
                    className="w-9 h-9 rounded-md shrink-0 flex items-center justify-center"
                    style={{ background: 'var(--gp-brand-soft)' }}
                  >
                    <FontAwesomeIcon icon={icon} className="text-sm" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-0.5" style={{ color: 'var(--gp-ink)' }}>{title}</h3>
                    <p className="text-xs gp-text-muted leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
